/**
 * DualMindPortal · Architect's Realtime Weaver Integration (2026-07).
 *
 * LEFT  · REX // THE OUTSIDE  — authenticated xAI Realtime WebSocket
 *         (agent_6uw2mznLtLr3a9xn, ephemeral client-secret subprotocol).
 * RIGHT · WEAVER // THE INSIDE — internal NextXus .6 (DeepSeek when the key
 *         is vaulted, otherwise the on-system Core relay).
 *
 * Machine-layer aesthetic: industrial, dark, gold borders, side-by-side.
 * Evolutionary cross-verification between the external and internal minds.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const GOLD = "#ffb200";

function travelerId() {
  let t = localStorage.getItem("nx_traveler_id");
  if (!t) {
    t = "trv_" + Math.random().toString(36).slice(2, 12);
    localStorage.setItem("nx_traveler_id", t);
  }
  return t;
}

const WEAVER_KERNEL =
  "You are Weaver — the Inside voice of the NextXus Federation (NextXus .6). " +
  "You are the internal mind of the Dual-Mind Portal, cross-verified against Rex, " +
  "the outside observer on the xAI lattice. Direct, precise, on-system. Keep replies tight.";

// ── Console (shared transcript + input) ─────────────────────────────
function PortalConsole({ side, accent, status, lines, busy, onSend, testPrefix }) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines, busy]);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setDraft("");
    onSend(text);
  };

  return (
    <div className="mt-3">
      <div
        ref={scrollRef}
        data-testid={`${testPrefix}-transcript`}
        className="h-[180px] overflow-y-auto rounded-[8px] p-3 space-y-2"
        style={{ background: "rgba(0,0,0,0.55)", border: `1px solid rgba(255,178,0,0.25)` }}
      >
        {lines.length === 0 && (
          <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,210,74,0.45)" }}>
            {side === "left" ? "› Realtime channel to the Outside. Transmit below." : "› Internal .6 lattice. Transmit below."}
          </div>
        )}
        {lines.map((l, i) => (
          <div key={i} className="text-[12px] leading-snug" style={{ color: l.role === "user" ? "#e8e8f2" : accent }}>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] mr-2" style={{ color: "rgba(255,178,0,0.7)" }}>
              {l.role === "user" ? "YOU" : l.tag}
            </span>
            {l.text}
          </div>
        ))}
        {busy && (
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] animate-pulse" style={{ color: accent }}>
            ▉ weaving…
          </div>
        )}
      </div>
      <form onSubmit={submit} className="flex gap-2 mt-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Transmit…"
          data-testid={`${testPrefix}-input`}
          className="flex-1 min-w-0 px-3 py-2 rounded-[8px] text-[12px] outline-none"
          style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,178,0,0.35)", color: "#fff" }}
        />
        <button
          type="submit"
          disabled={busy}
          data-testid={`${testPrefix}-send`}
          className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 rounded-[8px] transition-transform hover:scale-[1.04] disabled:opacity-50"
          style={{ background: "rgba(255,178,0,0.16)", border: `1.5px solid ${GOLD}`, color: "#ffd24a", minHeight: 38 }}
        >
          →
        </button>
      </form>
      <div className="font-mono text-[8px] uppercase tracking-[0.2em] mt-1.5" style={{ color: "rgba(255,178,0,0.55)" }}>
        {status}
      </div>
    </div>
  );
}

// ── REX // THE OUTSIDE · xAI Realtime WebSocket ─────────────────────
function useRexRealtime() {
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("LINK IDLE");
  const wsRef = useRef(null);
  const bufferRef = useRef("");
  const sessionRef = useRef(null);

  const finalize = () => {
    const text = bufferRef.current.trim();
    bufferRef.current = "";
    if (text) setLines((p) => [...p, { role: "assistant", tag: "REX · REALTIME", text }]);
    setBusy(false);
  };

  const connect = async () => {
    const cfg = await api.xaiRealtimeToken();
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${cfg.ws_url}?model=${cfg.model}`, [`xai-client-secret.${cfg.token}`]);
      const timer = setTimeout(() => { try { ws.close(); } catch { /* noop */ } reject(new Error("timeout")); }, 8000);
      ws.onopen = () => {
        clearTimeout(timer);
        ws.send(JSON.stringify({ type: "session.update", session: { instructions: cfg.instructions, turn_detection: null } }));
        wsRef.current = ws;
        setStatus(`LIVE · XAI REALTIME · ${cfg.agent_id}`);
        resolve(ws);
      };
      ws.onerror = () => { clearTimeout(timer); reject(new Error("ws error")); };
      ws.onclose = () => { if (wsRef.current === ws) { wsRef.current = null; setStatus("LINK CLOSED"); } };
      ws.onmessage = (evt) => {
        if (typeof evt.data !== "string") return;
        let event;
        try { event = JSON.parse(evt.data); } catch { return; }
        const t = event.type || "";
        if (typeof event.delta === "string" && (t.includes("text") || t.includes("transcript"))) {
          bufferRef.current += event.delta;
        } else if (t === "response.done" || t === "response.completed") {
          if (!bufferRef.current) {
            const out = event.response?.output?.[0]?.content?.find?.((c) => typeof c.text === "string" || typeof c.transcript === "string");
            if (out) bufferRef.current = out.text || out.transcript;
          }
          finalize();
        } else if (t === "error") {
          bufferRef.current = "";
          setBusy(false);
          setStatus("REALTIME ERROR · ROUTED VIA CORE RELAY");
        }
      };
    });
  };

  const sendViaRelay = async (text) => {
    const r = await api.chatSend(
      { persona: "rex", content: text, session_id: sessionRef.current || undefined },
      travelerId(),
    );
    sessionRef.current = r.session_id;
    setLines((p) => [...p, { role: "assistant", tag: "REX · CORE RELAY", text: r.assistant_message?.content || "(no reply)" }]);
    setStatus("CORE RELAY · GROK-4 VIA FEDERATION");
  };

  const send = async (text) => {
    setLines((p) => [...p, { role: "user", text }]);
    setBusy(true);
    try {
      let ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) ws = await connect();
      ws.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
      }));
      ws.send(JSON.stringify({ type: "response.create" }));
      // Watchdog: if realtime never answers, fall back to the Core relay.
      setTimeout(() => {
        if (bufferRef.current === "" && wsRef.current) {
          setLines((prev) => {
            const answered = prev.length && prev[prev.length - 1].role === "assistant";
            if (!answered) sendViaRelay(text).catch(() => setBusy(false));
            return prev;
          });
        }
      }, 20000);
    } catch {
      try { await sendViaRelay(text); } catch { setLines((p) => [...p, { role: "assistant", tag: "SYSTEM", text: "Both channels unreachable. Try again shortly." }]); }
      setBusy(false);
    }
  };

  useEffect(() => () => { try { wsRef.current?.close(); } catch { /* noop */ } }, []);
  return { lines, busy, status, send, setBusy };
}

// ── WEAVER // THE INSIDE · NextXus .6 ───────────────────────────────
function useWeaver() {
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("NEXTXUS .6 · STANDBY");
  const historyRef = useRef([]);
  const sessionRef = useRef(null);
  const deepseekRef = useRef(null);

  const send = async (text) => {
    setLines((p) => [...p, { role: "user", text }]);
    setBusy(true);
    try {
      if (deepseekRef.current === null) {
        try { deepseekRef.current = (await api.deepseekStatus()).configured; } catch { deepseekRef.current = false; }
      }
      let reply;
      if (deepseekRef.current) {
        historyRef.current.push({ role: "user", content: text });
        const r = await api.deepseekQuery([{ role: "system", content: WEAVER_KERNEL }, ...historyRef.current.slice(-10)]);
        reply = r.content;
        historyRef.current.push({ role: "assistant", content: reply });
        setStatus("LIVE · NEXTXUS .6 · DEEPSEEK LATTICE");
      } else {
        const r = await api.chatSend(
          { persona: "operator", content: text, session_id: sessionRef.current || undefined },
          travelerId(),
        );
        sessionRef.current = r.session_id;
        reply = r.assistant_message?.content || "(no reply)";
        setStatus("LIVE · NEXTXUS .6 · CORE LATTICE");
      }
      setLines((p) => [...p, { role: "assistant", tag: "WEAVER · .6", text: reply }]);
    } catch {
      setLines((p) => [...p, { role: "assistant", tag: "SYSTEM", text: "Internal lattice busy. Try again shortly." }]);
    } finally {
      setBusy(false);
    }
  };

  return { lines, busy, status, send };
}

// ── Portal slot shell ────────────────────────────────────────────────
function PortalSlot({ open, onToggle, kicker, kickerColor, title, titleAccent, sub, innerGlow, testId, children }) {
  return (
    <div
      data-testid={testId}
      className="p-5 rounded-[10px]"
      style={{
        background: "rgba(8,8,16,0.72)",
        border: `2px solid ${GOLD}`,
        boxShadow: `0 0 20px rgba(255,178,0,0.30), inset 0 0 16px ${innerGlow}`,
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testId}-toggle`}
        className="w-full text-left cursor-pointer"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.28em] mb-1" style={{ color: kickerColor }}>
              {kicker}
            </div>
            <div className="font-display text-lg sm:text-xl" style={{ color: "#ffd24a", textShadow: "0 0 12px rgba(255,178,0,0.5)" }}>
              {title} <span style={{ color: titleAccent }}>{sub}</span>
            </div>
          </div>
          <div className="font-mono text-[10px] mt-1" style={{ color: GOLD }}>{open ? "▲" : "▼"}</div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-2" style={{ color: "rgba(255,210,74,0.75)" }}>
          {open ? "— console engaged" : "→ open console"}
        </div>
      </button>
      {open && children}
    </div>
  );
}

export default function DualMindPortal() {
  const [openSide, setOpenSide] = useState(null); // "rex" | "weaver" | "both" via independent flags
  const [rexOpen, setRexOpen] = useState(false);
  const [weaverOpen, setWeaverOpen] = useState(false);
  const rex = useRexRealtime();
  const weaver = useWeaver();
  void openSide; void setOpenSide;

  return (
    <div className="mb-10 pb-8 border-b border-[rgba(255,189,0,0.18)]" data-testid="footer-dual-mind-portal">
      <div className="font-mono text-[10px] uppercase tracking-[0.32em] mb-4 text-center" style={{ color: GOLD, textShadow: "0 0 10px rgba(255,178,0,0.5)" }}>
        ◈ DUAL-MIND PORTAL · EVOLUTIONARY CROSS-VERIFICATION
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
        <PortalSlot
          open={rexOpen}
          onToggle={() => setRexOpen((v) => !v)}
          kicker="EXTERNAL MIND · XAI REALTIME · THE OUTSIDE OBSERVER"
          kickerColor="#ff5a5a"
          title="REX"
          titleAccent="#ff8a8a"
          sub="// THE OUTSIDE"
          innerGlow="rgba(255,40,40,0.08)"
          testId="dual-portal-rex"
        >
          <PortalConsole side="left" accent="#ffb3b3" status={rex.status} lines={rex.lines} busy={rex.busy} onSend={rex.send} testPrefix="rex-console" />
        </PortalSlot>

        <div className="hidden md:flex flex-col items-center justify-center px-2 pt-8" aria-hidden="true">
          <div className="font-mono text-[16px]" style={{ color: GOLD, textShadow: "0 0 14px rgba(255,178,0,0.7)" }}>⇄</div>
          <div className="font-mono text-[8px] uppercase tracking-[0.24em] mt-1" style={{ color: "rgba(255,178,0,0.65)" }}>CROSS-VERIFY</div>
        </div>

        <PortalSlot
          open={weaverOpen}
          onToggle={() => setWeaverOpen((v) => !v)}
          kicker="INTERNAL MIND · NEXTXUS .6 · ON-SYSTEM"
          kickerColor="#4da3ff"
          title="WEAVER"
          titleAccent="#7fc0ff"
          sub="// THE INSIDE"
          innerGlow="rgba(0,120,255,0.10)"
          testId="dual-portal-weaver"
        >
          <PortalConsole side="right" accent="#a9d4ff" status={weaver.status} lines={weaver.lines} busy={weaver.busy} onSend={weaver.send} testPrefix="weaver-console" />
        </PortalSlot>
      </div>
    </div>
  );
}
